/**
 * A YAML document in a box: the server's text, edited in place, saved whole.
 * The repository's environment and a declared system both use it. The box
 * takes the server's next text unless the person has edited, and after a
 * save it takes the text the server answers with, re-rendered.
 *
 * A line-number gutter runs beside the box, scrolled with it. The line a
 * refused save names and the line a failed step came from are marked there,
 * and a step's "line N" brings its line into view (`reveal`).
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/index.js";
import "./repositoryEnvironment.css";

export interface YamlEditorError {
  message: string;
  line?: number;
}

const MIN_ROWS = 18;

/** Character offsets of a 1-based line, clamped to the text. */
export function lineRange(text: string, line: number): { start: number; end: number } | null {
  const lines = text.split("\n");
  if (!Number.isInteger(line) || line < 1 || line > lines.length) return null;
  let start = 0;
  for (let index = 0; index < line - 1; index += 1) start += lines[index]!.length + 1;
  return { start, end: start + lines[line - 1]!.length };
}

export function yamlErrorLine(error: YamlEditorError): string {
  return error.line ? `Line ${error.line}: ${error.message}` : error.message;
}

export interface YamlEditorProps {
  /** The block's label, and the box's name ("<title> YAML"). */
  title: string;
  fileName: string;
  /** Who declared the text, or where it came from. */
  originLine: string;
  /** The server's text. */
  text: string;
  /** What the server text is a revision of: a new one resyncs even when the text is the same. */
  revision?: string | number | null;
  /** A request or a run is in flight. */
  busy: boolean;
  error: YamlEditorError | null;
  saveLabel: string;
  /** Resolves true when the text was taken. */
  onSave: (text: string) => Promise<boolean>;
  reset?: { label: string; onReset: () => void } | null;
  copyLabel?: string;
  /** Save is pressable on the server's own text (a new document saved as offered). */
  saveUnchanged?: boolean;
  /** A line to mark in the gutter, such as the line a failed step came from. */
  markLine?: number | null;
  /** Bring this line into view and select it; a new `at` asks again for the same line. */
  reveal?: { line: number; at: number } | null;
  testIdPrefix: string;
}

export function YamlEditor({
  title,
  fileName,
  originLine,
  text: serverText,
  revision,
  busy,
  error,
  saveLabel,
  onSave,
  reset,
  copyLabel,
  saveUnchanged,
  markLine,
  reveal,
  testIdPrefix,
}: YamlEditorProps) {
  const [text, setText] = useState(serverText);
  const [said, setSaid] = useState<string | null>(null);
  // The server text the box last took, and whether a save is waiting for the
  // text it answers with: the saved text comes back re-rendered.
  const baseline = useRef(serverText);
  const saving = useRef(false);
  const current = useRef(text);
  current.current = text;

  useEffect(() => {
    if (saving.current || current.current === baseline.current) setText(serverText);
    baseline.current = serverText;
    saving.current = false;
  }, [serverText, revision]);

  const box = useRef<HTMLTextAreaElement>(null);
  const gutter = useRef<HTMLPreElement>(null);

  useEffect(() => {
    const area = box.current;
    if (!reveal || !area) return;
    const range = lineRange(area.value, reveal.line);
    if (!range) return;
    // The box grows to fit every line, so it never scrolls on its own: the page
    // does. The gutter's number for the line sits level with it.
    gutter.current
      ?.querySelector<HTMLElement>(`[data-line="${reveal.line}"]`)
      ?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    area.focus({ preventScroll: true });
    area.setSelectionRange(range.start, range.end);
  }, [reveal]);

  const lineCount = text.split("\n").length;
  const rows = Math.max(MIN_ROWS, lineCount + 1);
  const errorLine = error?.line;

  return (
    <div className="blk qa-yaml-block" data-testid={testIdPrefix}>
      <div className="bh">
        <span className="label">{title}</span>
        <span className="fact">{fileName}</span>
      </div>
      <span className="qa-hint" data-testid={`${testIdPrefix}-origin`}>
        {originLine}
      </span>
      <div className="qa-yaml-frame">
        <pre
          ref={gutter}
          className="qa-yaml-gutter"
          aria-hidden="true"
          data-testid={`${testIdPrefix}-gutter`}
        >
          {Array.from({ length: lineCount }, (_, index) => {
            const line = index + 1;
            return (
              <span
                key={line}
                data-line={line}
                className={
                  line === errorLine ? "is-error" : line === markLine ? "is-mark" : undefined
                }
              >
                {line}
              </span>
            );
          })}
        </pre>
        <textarea
          ref={box}
          className="qa-yaml"
          aria-label={`${title} YAML`}
          spellCheck={false}
          rows={rows}
          value={text}
          onScroll={(event) => {
            if (gutter.current) gutter.current.scrollTop = event.currentTarget.scrollTop;
          }}
          onChange={(event) => {
            setText(event.target.value);
            setSaid(null);
          }}
        />
      </div>
      {error && (
        <p role="alert" className="qa-notice" data-testid={`${testIdPrefix}-error`}>
          {yamlErrorLine(error)}
        </p>
      )}
      <div className="qa-attention-actions">
        <Button
          disabled={busy || (!saveUnchanged && text === serverText)}
          onClick={() => {
            saving.current = true;
            void onSave(text).then((saved) => {
              if (!saved) saving.current = false;
            });
          }}
        >
          {saveLabel}
        </Button>
        {reset && (
          <button type="button" className="wrk-quiet-btn" disabled={busy} onClick={reset.onReset}>
            {reset.label}
          </button>
        )}
        <button
          type="button"
          className="wrk-quiet-btn"
          onClick={() => {
            const clipboard = navigator.clipboard;
            if (!clipboard) {
              setSaid("Could not copy");
              return;
            }
            clipboard.writeText(text).then(
              () => setSaid("Copied"),
              () => setSaid("Could not copy"),
            );
          }}
        >
          {said ?? copyLabel ?? `Copy as ${fileName}`}
        </button>
      </div>
    </div>
  );
}
